import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import InputField from "components/fields/InputField";
import { useFormik } from "formik";
import * as Yup from "yup";
import authImg from "assets/img/auth/logo.svg";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import Request from "helpers/request";

function Password() {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .max(20, "Password must not exceed 20 characters")
      .matches(
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&_])[A-Za-z\d@$!%*?&_]+$/,
        "Password must include at least one uppercase letter, one lowercase letter, one number, and one special character (including underscore)"
      )
      .required("Password is required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  const formik = useFormik({
    initialValues: {
      password: "",
      confirm_password: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // set password action here
      setValidationErrors({});
      setIsSubmitting(true);
      Request.postJson("user/set-password", values,{}, {
          headers: {
            token: `${token}`,
          },
      })
        .then((r) => {
          setIsSubmitting(false);
          navigate("/sign-in");
        })
        .catch((e) => {
          setIsSubmitting(false);
          if (e.response.data.detail !== undefined) {
            setValidationErrors(e.response.data.detail);
          }
          if (e.response.data.error !== undefined) {
            setValidationErrors(e.response.data.error);
          }
          console.log(e);
        });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-center">
        {/* Sign in section */}
        <div className="mt-[10vh] w-full max-w-full flex-col items-center rounded-xl p-6 shadow-[0_0px_6px_rgba(0,0,0,0.1)] xl:max-w-[420px]">
          <img className="mt-[-70px] mr-auto ml-auto bg-white rounded-[50px]" src={authImg} alt="" />
          <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
            Set Password
          </h4>
          <p className="mb-4 ml-1 text-base text-gray-600">
            Enter password to register!
          </p>
          {Object.keys(validationErrors).length !== 0 && (
            <small className="mb-3 ml-1 text-center text-red-600">
              {validationErrors.message}
            </small>
          )}
          {/* Password */}
          <div className="relative mb-3">
            <InputField
              required="*"
              variant="auth"
              extra=""
              label="Password"
              placeholder="Min. 8 characters"
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formik.values.password}
              onchange={formik.handleChange}
              state={
                formik.touched.password && formik.errors.password ? "error" : ""
              }
            />
            <span
              className={`absolute top-11 right-2 cursor-pointer text-gray-600`}
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <AiFillEye /> : <AiFillEyeInvisible />}
            </span>
            {formik.touched.password && formik.errors.password && (
              <small className="ml-1.5 text-[12px] text-red-600">
                {formik.errors.password}
              </small>
            )}
          </div>

          {/* Confirm Password */}
          <div className="relative mb-3">
            <InputField
              required="*"
              variant="auth"
              extra=""
              label="Confirm Password"
              placeholder="Confirm your password"
              id="confirm_password"
              type="password"
              name="confirm_password"
              value={formik.values.confirm_password}
              onchange={formik.handleChange}
              state={
                formik.touched.confirm_password &&
                formik.errors.confirm_password
                  ? "error"
                  : ""
              }
            />
            {formik.touched.confirm_password &&
              formik.errors.confirm_password && (
                <small className="ml-1.5 text-[12px] text-red-600">
                  {formik.errors.confirm_password}
                </small>
              )}
          </div>
          {/* Checkbox */}
          <button
            disabled={isSubmitting}
            type="submit"
            className="linear mt-5 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            {isSubmitting ? "Setting Password..." : "Proceed"}
          </button>
        </div>
      </div>
    </form>
  );
}
export default Password;
